import { HttpClient } from '@angular/common/http';
import { toHttpParams } from './http-params';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import type { ChannelPrefs } from './varsler.service';
import type { PostSummary, ReadStats } from './oppslag.service';

export interface DashboardActivityItem {
  type: string;
  title: string;
  atUtc: string;
}

/** Weekly counts for the KPI sparklines — 8 buckets, oldest first, the last one is the current week. */
export interface DashboardSeries {
  saker: number[];
  bookinger: number[];
  kommunikasjon: number[];
}

export interface StyreDashboard {
  openSaker: number;
  oppgaverMedFrist: number;
  kommendeBookinger: number;
  nyligKommunikasjon: number;
  activity: DashboardActivityItem[];
  series?: DashboardSeries;
}

export interface RegisterRow {
  membershipId: string;
  unitId: string;
  unitLabel: string;
  andelSeksjonNumber?: string | null;
  areal?: number | null;
  ownerUserId: string;
  role: string;
  parkering: string[];
  bod: string[];
  /** Resolved beboer display name (empty when no profile/membership). */
  ownerName: string;
  /** Contact phone from the profile, when shared. */
  contact?: string | null;
  /** Membership lifecycle: 'active' | 'invited' (drives the status badge). */
  activationState: string;
}

export interface RegisterPage {
  rows: RegisterRow[];
  total: number;
}

export interface RegisterQuery {
  q?: string;
  role?: string;
  sort?: 'unit' | 'role';
  page?: number;
  pageSize?: number;
}

export interface UnitEdit {
  label?: string;
  areal?: number;
  bod?: string[];
  parkering?: string[];
  lader?: string[];
}

export interface BrandingView {
  displayName?: string | null;
  accentColorToken?: string | null;
  logoObjectKey?: string | null;
}

export interface TenantSettingsView {
  name: string;
  branding: BrandingView;
  enabledModules: string[];
  notificationDefaults: Record<string, ChannelPrefs>;
  /** Subscription plan (start | standard | komplett | forvalter) — V1.12. */
  plan: string;
  /** Modules the plan does NOT unlock (the lagadmin editor clamps toggles to exclude these). */
  lockedModules: string[];
}

/**
 * The customer-app bootstrap config (min-konfigurasjon): the effective enabled modules (toggle ∩ plan
 * ceiling), the plan, and the plan-locked modules (V1.12) so the client can lock features + show an
 * upgrade affordance. Plus branding.
 */
export interface TenantConfigView {
  enabledModules: string[];
  branding: BrandingView;
  plan: string;
  lockedModules: string[];
  /** The lag's default language (`nb` | `nn` | `en`), or null when unset — used only for users with no choice of their own. */
  defaultLocale?: string | null;
}

/**
 * Styre console client (`/api/styre/**` + `/api/min-konfigurasjon`). Hand-written to mirror the
 * Phase M8 backend contract. The lagadministrator settings touch only customer-owned tenant
 * fields; the white-label config (modules + branding + logo) feeds the resident app at load.
 */
@Injectable({ providedIn: 'root' })
export class StyreService {
  private readonly http = inject(HttpClient);

  register(query: RegisterQuery = {}): Observable<RegisterPage> {
    const params = toHttpParams(query);
    return this.http.get<RegisterPage>('/api/styre/register', { params });
  }

  updateUnit(unitId: string, edit: UnitEdit): Observable<void> {
    return this.http.put<void>(`/api/styre/register/${encodeURIComponent(unitId)}`, edit);
  }

  eierskifte(outgoingUserId: string, incomingEmail: string, role?: string): Observable<{ endedMembershipId: string; invitationId: string }> {
    return this.http.post<{ endedMembershipId: string; invitationId: string }>('/api/styre/register/eierskifte', { outgoingUserId, incomingEmail, role });
  }

  invitasjon(unitId: string, email: string, role?: string): Observable<{ id: string }> {
    return this.http.post<{ id: string }>('/api/styre/register/invitasjon', { unitId, email, role });
  }

  /** The tenant-scoped CSV export URL. */
  exportUrl(): string {
    return '/api/styre/register/eksport';
  }

  /**
   * Download the register CSV through HttpClient so the bearer token travels with the request
   * (a plain `window.open` navigation carries no Authorization header and 401s).
   */
  exportCsv(): Observable<Blob> {
    return this.http.get(this.exportUrl(), { responseType: 'blob' });
  }

  masseutsendelse(audience: 'alle' | 'eiere', category: string, title: string, body: string, importance?: string): Observable<{ id: string }> {
    return this.http.post<{ id: string }>('/api/styre/register/masseutsendelse', { audience, category, title, body, importance });
  }

  // ── Kommunikasjon (thin M2 wrapper) ──
  publishKommunikasjon(payload: { category: string; title: string; body: string; importance?: string; targetingScope?: string; targetingIds?: string[]; commentsEnabled?: boolean; publishAtUtc?: string; expiresAtUtc?: string }): Observable<{ id: string }> {
    return this.http.post<{ id: string }>('/api/styre/kommunikasjon', payload);
  }

  outbox(): Observable<PostSummary[]> {
    return this.http.get<PostSummary[]>('/api/styre/kommunikasjon/sendt');
  }

  readStats(id: string): Observable<ReadStats> {
    return this.http.get<ReadStats>(`/api/styre/kommunikasjon/${encodeURIComponent(id)}/lest-statistikk`);
  }

  // ── Innstillinger (lagadministrator) ──
  updateModules(modules: string[]): Observable<void> {
    return this.http.put<void>('/api/styre/innstillinger/moduler', { modules });
  }

  updateBranding(accentColorToken?: string, displayName?: string): Observable<void> {
    return this.http.put<void>('/api/styre/innstillinger/branding', { accentColorToken, displayName });
  }

  uploadLogo(file: File): Observable<{ id: string }> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<{ id: string }>('/api/styre/innstillinger/logo', form);
  }

  updateNotificationDefaults(matrix: Record<string, ChannelPrefs>): Observable<void> {
    return this.http.put<void>('/api/styre/innstillinger/varslingsstandarder', { matrix });
  }

  /**
   * Sets the lag's default language — what new residents meet before they choose their own
   * (`null` clears it). Board-only.
   */
  updateDefaultLocale(locale: string | null): Observable<void> {
    return this.http.put<void>('/api/styre/innstillinger/sprak', { locale });
  }

  // ── Customer-app bootstrap config ──
  /** The white-label logo URL for the current tenant (served via an `<img src>`). */
  logoUrl(): string {
    return '/api/min-konfigurasjon/logo';
  }
}
